import React, { useContext, useState } from "react";
import { useFormContext } from "../createworkflow/FormContext";
import MenuContext from "./MenuContext";
import { displayMessage } from "../../Utils/helper";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { SubmitWorkflowPayloadPrep, stageLayoutStyles } from "./helper";
import { sumbitWorkflow } from "../../services/ApiWorkflow";
import { useNavigate } from "react-router-dom";
import Loader from "../common/Loader";
import {
  ClipboardList,
  CalendarRange,
  Database,
  Layers,
  Brain,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

export default function Stage7() {
  const { setActiveTab } = useContext(MenuContext);
  const { formData } = useFormContext();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const isFeatureMode =
    formData?.stage3?.forecastingType === "feature" &&
    Boolean(formData?.stage3?.featureBasedForecasting?.selected_combinations?.length);
  const selectedFeatureGroups = formData?.stage3?.featureBasedForecasting?.selected_combinations?.length;

  const EditButtonClickHandler = () => setActiveTab("Stage6");

  const SubmitButtonClickHandler = async () => {
    if (!formData.stage1?.workflow_name) {
      displayMessage("danger", "Workflow name is mandatory");
      return;
    }
    if (!formData.stage1?.data_upload_id) {
      displayMessage("danger", "Please choose upload type for file upload");
      return;
    }
    if (!formData.stage2?.data_frequency) {
      displayMessage("danger", "Data Frequency is mandatory");
      return;
    }
    if (!formData.stage2?.forecast_period) {
      displayMessage("danger", "Forecast Period is mandatory");
      return;
    }
    if (!formData.stage3?.products?.length) {
      displayMessage("danger", "Please select at least one product");
      return;
    }
    if (!formData.stage3?.geography?.length) {
      displayMessage("danger", "Please select at least one geography");
      return;
    }
    if (!formData.stage4?.combinations?.length) {
      displayMessage("danger", "Please select at least one combination");
      return;
    }
    if (!formData.stage6?.models?.length) {
      displayMessage("danger", "Please select at least one model");
      return;
    }

    setIsLoading(true);
    const payload = SubmitWorkflowPayloadPrep(formData);
    const result = await sumbitWorkflow(payload);

    if (!result) {
      displayMessage("danger", "Error occurred while submitting workflow");
    } else {
      sessionStorage.clear("formData");
      navigate("/ManageWorkflow");
      displayMessage("success", "Workflow submitted successfully");
    }

    setIsLoading(false);
  };

  const Section = ({ icon: Icon, title, children, columns = 2 }) => (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Icon size={18} strokeWidth={2.25} color="#fff" />
        <span style={styles.sectionTitle}>{title}</span>
      </div>
      <div
        style={{
          ...styles.sectionBody,
          gridTemplateColumns:
            columns === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
        }}
      >
        {children}
      </div>
    </div>
  );

  const Item = ({ label, value, fullWidth }) => (
    <div
      style={{
        ...styles.item,
        ...(fullWidth ? { gridColumn: "1 / -1" } : null),
      }}
    >
      <span style={styles.itemLabel}>{label}</span>
      <span style={styles.itemValue}>{value || "—"}</span>
    </div>
  );



  return (
    <ErrorBoundary>
      <div style={stageLayoutStyles.wrapper}>
        <div style={styles.pageCard}>
          <div style={styles.sections}>
            <Section icon={ClipboardList} title="Initialize Workflow">
              <Item
                label="Workflow Name"
                value={formData.stage1?.workflow_name}
              />
              <Item
                label="Description"
                value={formData.stage1?.workflow_description}
              />
            </Section>

            <Section icon={CalendarRange} title="Temporal Settings">
              <Item
                label="Data Frequency"
                value={formData.stage2?.data_frequency}
              />
              <Item
                label="Train Period (%)"
                value={formData.stage2?.train_percentage}
              />
              <Item
                label="Test Period (%)"
                value={formData.stage2?.test_percentage}
              />
              <Item
                label="Forecast Frequency"
                value={formData.stage2?.forecast_frequency}
              />
              <Item
                label="Forecast Period"
                value={formData.stage2?.forecast_period}
                fullWidth
              />
            </Section>

            <Section icon={Database} title="Data Selection">
              <Item
                label="Selected SKUs"
                value={formData.stage3?.products?.length}
              />
              <Item
                label="Selected Locations"
                value={formData.stage3?.geography?.length}
              />
            </Section>

            <Section icon={Layers} title="Data Availability" columns={1}>
              {isFeatureMode ? (
                <>
                  <Item label="Selected Feature Groups" value={selectedFeatureGroups} />
                  <Item
                    label="Selected Combinations (SKU x Location)"
                    value={formData.stage4?.combinations?.length}
                  />
                </>
              ) : (
                <Item
                  label="Selected Combinations"
                  value={formData.stage4?.combinations?.length}
                />
              )}
            </Section>

            <Section icon={Brain} title="Model Selection" columns={1}>
              <Item
                label="Models Chosen"
                value={formData.stage6?.models?.join(", ")}
              />
            </Section>
          </div>

          {isLoading ? (
            <div style={styles.loaderWrap}>
              <Loader size="large" />
            </div>
          ) : (
            <div style={styles.actions}>
              <button
                type="button"
                onClick={EditButtonClickHandler}
                style={styles.editBtn}
              >
                <ArrowLeft size={16} strokeWidth={2.25} />
                Edit
              </button>
              <button
                type="button"
                onClick={SubmitButtonClickHandler}
                style={styles.submitBtn}
              >
                Submit
                <ArrowRight size={16} strokeWidth={2.25} />
              </button>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

const styles = {
  pageCard: {
    width: "100%",
    background: "#fff",
    padding: "16px 20px 20px",
    borderRadius: 12,
    boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    border: "1px solid #e6eef4",
    boxSizing: "border-box",
  },
  sections: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  section: {
    borderRadius: 10,
    border: "2px solid #032B4E",
    overflow: "hidden",
    background: "#032B4E",
    boxShadow: "0 2px 8px rgba(12, 60, 84, 0.06)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#032B4E",
    color: "#fff",
    padding: "10px 14px",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    color: "#fff",
    lineHeight: 1.2,
  },
  sectionBody: {
    display: "grid",
    gap: 10,
    padding: 12,
    background: "#fff",
  },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    background: "#f6fafd",
    border: "1px solid #e6eef4",
    borderRadius: 8,
    padding: "10px 12px",
    minWidth: 0,
  },
  itemLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
    lineHeight: 1.2,
  },
  itemValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#0c3c54",
    fontFamily: "Poppins, sans-serif",
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "28px 0",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 18,
    paddingTop: 16,
    borderTop: "1px solid #e6eef4",
  },
  editBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    color: "#032B4E",
    background: "#fff",
    border: "1.5px solid #032B4E",
    borderRadius: 8,
    cursor: "pointer",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    color: "#fff",
    background: "#E06639",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
  },
};
